import {Component, Inject, ChangeDetectionStrategy} from '@angular/core';
import {FormControl, FormGroupDirective, NgForm} from '@angular/forms';
import {ErrorStateMatcher} from '@angular/material/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {type IFormattedTransaction, TransactionRefund} from '../../models';
import {
  Alignment,
  ConfirmationService,
  FormBuilder,
  FormData,
  FormField,
  FormValidation,
  InputType,
  MerchantService,
  MonekColour,
  NotificationService,
  Size,
  ValidationPattern,
  VTService,
  ErrorReferenceService,
} from '@odin/odin-core';
import {VTPaymentResponse} from '../../models/vt-payment';
import {CurrencyPipe} from '@angular/common';
import type {AccountOption} from '../account-selector/account-selector.component';

export class RefundModalData {
  constructor(public transaction: IFormattedTransaction) {}
}

/** Error when invalid control is dirty, touched, or submitted. */
export class AmountErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(
    control: FormControl | null,
    form: FormGroupDirective | NgForm | null,
  ): boolean {
    const isSubmitted = form && form.submitted;
    return !!(
      control &&
      control.invalid &&
      (control.dirty || control.touched || isSubmitted)
    );
  }
}

@Component({
  standalone: false,
  selector: 'odin-refund-transaction-modal',
  templateUrl: './refund-transaction-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./refund-transaction-modal.component.scss'],
})
export class RefundTransactionModalComponent {
  private formattedTransaction: IFormattedTransaction;
  public refundForm: FormData<TransactionRefund>;
  public amountMatcher = new AmountErrorStateMatcher();
  public processing: boolean = false;
  public remainingAmount: number;
  /** MonekID the refund will be processed against; defaults to the original transaction's. */
  public selectedMonekId: string;

  constructor(
    private merchantService: MerchantService,
    public dialogRef: MatDialogRef<RefundTransactionModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: RefundModalData,
    private VTService: VTService,
    private notifcationService: NotificationService,
    private errorReference: ErrorReferenceService,
    private confirmationService: ConfirmationService,
    private currencyPipe: CurrencyPipe,
  ) {
    this.formattedTransaction = this.data.transaction;
    this.selectedMonekId = this.formattedTransaction.merchant?.monekId ?? '';

    this.remainingAmount =
      this.formattedTransaction.remainingValue !== null &&
      this.formattedTransaction.remainingValue !== undefined
        ? Number.parseFloat(this.formattedTransaction.remainingValue.toFixed(2))
        : this.formattedTransaction.amount;

    this.refundForm = this.BuildForm();
  }

  public onAccountSelected(account: AccountOption): void {
    this.selectedMonekId = account.monekId;
    this.refundForm.formFields
      .find((f: FormField) => f.id === 'MonekID')
      ?.SetValue(account.monekId);
  }

  private BuildForm(): FormData<TransactionRefund> {
    const userProfile = this.merchantService.getUserprofile();
    const formData = new FormBuilder<TransactionRefund>(MonekColour.Primary)
      .ToggleSubmitButton(true)
      .AddFormField(
        'OperatorId',
        '',
        InputType.Hidden,
        userProfile?.userId || '',
        Size.Full,
      )
      .AddFormField(
        'OperatorName',
        '',
        InputType.Hidden,
        userProfile?.email || '',
        Size.Full,
      )
      .SubmitButtonConfig('Refund', Size.Full, Alignment.Center)
      .AddFormField(
        'PaymentChannel',
        '',
        InputType.Hidden,
        this.formattedTransaction.channel,
        Size.Auto,
      )
      .AddFormField('AuxiliaryData', '', InputType.Hidden, '', Size.Auto)
      .AddFormField('MerchantID', '', InputType.Hidden, '', Size.Auto)
      .AddFormField('CountryCode', '', InputType.Hidden, this.formattedTransaction.merchantCountry, Size.Auto)
      .AddFormField('CurrencyCode', '', InputType.Hidden, this.formattedTransaction.currencyCode, Size.Auto)
      .AddFormField('ValidityID', '', InputType.Hidden, null, Size.Auto)
      .AddFormField(
        'CrossReference',
        '',
        InputType.Hidden,
        this.formattedTransaction.crossReference,
        Size.Auto,
      )
      .AddFormField('MonekID', '', InputType.Hidden, this.selectedMonekId, Size.Auto)
      .AddFormField(
        'Amount',
        `Amount to refund (${this.currencyPipe.transform(
          this.remainingAmount,
          this.formattedTransaction.currencyCountry,
        )} available)`,
        InputType.Number,
        null,
        Size.Full,
        new FormValidation()
          .Required('Amount is required')
          .MinValue(
            0.01,
            `Amount must be greater than ${this.currencyPipe.transform(
              0.01,
              this.formattedTransaction.currencyCountry,
            )}`,
          )
          .MaxValue(
            this.remainingAmount,
            `Amount can not exceed ${this.currencyPipe.transform(
              this.remainingAmount,
              this.formattedTransaction.currencyCountry,
            )}`,
          )
          .MaxLength(9)
          .PatternMatch(ValidationPattern.Decimal, 'Amount must be a number'),
        true,
        {},
        undefined,
        (formfield: FormField, value: string) => {
          if (value !== '') {
            const numericValue = parseFloat(value);
            if (isNaN(numericValue)) {
              formfield.SetValue('0.00');
            } else {
              formfield.SetValue(numericValue.toFixed(2));
            }
          }
        },
      )
      .AddFormField('paymentReference', 'Refund reference', InputType.Text, this.formattedTransaction.paymentReference, Size.Full, new FormValidation().MaxLength(100))
      .Build((data: TransactionRefund) => {
        this.makeRefund(data);
      });
    return formData;
  }

  private makeRefund(data: TransactionRefund): void {
    this.processing = true;

    const accountNote = this.selectedMonekId
      ? ` It will be processed on merchant account ${this.selectedMonekId}.`
      : '';

    this.confirmationService
      .ConfirmDecline(
        'Process this refund?',
        `The refund will be deducted from the original transaction and returned to the customer's card.${accountNote}`,
        'Refund',
        'Cancel',
        'primary',
        'warn',
      )
      .subscribe((resp: boolean) => {
        if (resp) this.executeRefund(data);
        else this.processing = false;
      });
  }
  private executeRefund(data: TransactionRefund): void {
    this.VTService.SubmitRefundRequest(data, this.selectedMonekId).subscribe(
      (_resp: VTPaymentResponse) => {
        if (_resp.status) {
          this.dialogRef.close(data);
          this.notifcationService.SmallDialog(
            'Refund completed',
            'Close',
            60000,
          );
        } else {
          const failure = new Error(_resp.message || 'Refund failed');
          // Keep the server's trace id as the reference; a locally minted one would match no log line.
          this.errorReference.recordBackendReference(failure, _resp.traceId);
          this.refundForm.ShowError(this.errorReference.formatMessage(failure.message, failure));
        }
        this.processing = false;
      },
      (err: unknown) => {
        this.refundForm.ShowError(this.errorReference.formatMessage('Something went wrong.', err));
        this.processing = false;
      },
    );
  }

  // close without saving
  public cancel(): void {
    this.dialogRef.close(null);
  }
}
